/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.markdown.parser

import systems.frictionless.impulse.markdown.model.Token

/**
 * Parser for a very limited subset of Markdown, which supports bold, italic, and hyperlinks only. Nested types are not supported.
 *
 * Input: `Hello *this* _is_ [markdown](https://www.somewhere.com)`
 * Output: listOf(Token("Hello ", Token.Type.Plain), Token("this", Token.Type.Bold), Token(" ", Token.Type.Plain), Token("is", Token.Type.Italic), Token(" ", Token.Type.Plain), Token("markdown", Token.Type.Link("https://www.somewhere.com")))
 */
object MarkdownParser {
    private const val BOLD_START = '*'
    private const val BOLD_STOP = '*'
    private const val ITALICS_START = '_'
    private const val ITALICS_STOP = '_'
    private const val LINK_TEXT_START = '['
    private const val LINK_TEXT_STOP = ']'
    private const val LINK_URL_START = '('
    private const val LINK_URL_STOP = ')'

    /**
     * @param markdownString A string containing a subset of Markdown.
     * @return A list of markdown tokens.
     */
    fun parse(markdownString: String): List<Token> {
        val result = mutableListOf<Token>()
        val iterator = markdownString.iterator()
        val plainTextContent = StringBuilder()
        iterator.forEach { char ->
            when (char) {
                BOLD_START -> {
                    addPlainTextTokenIfAny(plainTextContent, result)
                    result += parseText(iterator, BOLD_START, BOLD_STOP, Token.Type.Bold)
                }
                ITALICS_START -> {
                    addPlainTextTokenIfAny(plainTextContent, result)
                    result += parseText(iterator, ITALICS_START, ITALICS_STOP, Token.Type.Italic)
                }
                LINK_TEXT_START -> {
                    addPlainTextTokenIfAny(plainTextContent, result)
                    result += parseLink(iterator)
                }
                else -> {
                    plainTextContent.append(char)
                }
            }
        }

        addPlainTextTokenIfAny(plainTextContent, result)

        return mergePlainTextTokensAndRemoveEmptyTokens(result)
    }

    /**
     * Performs optimization of result list.
     * 1. merges plain text tokens if there are multiple in a row
     * 2. removes tokens with no text
     */
    @Suppress("LiftReturnOrAssignment")
    private fun mergePlainTextTokensAndRemoveEmptyTokens(resultWithPossibleDuplicates: MutableList<Token>): List<Token> {
        val result = mutableListOf<Token>()
        resultWithPossibleDuplicates.forEach { current ->
            if (result.isNotEmpty()) {
                val last = result.last()
                if (last.type == Token.Type.Plain && current.type == Token.Type.Plain) {
                    val mergedToken = Token(last.text + current.text, Token.Type.Plain)
                    result.remove(last)
                    result += mergedToken
                } else {
                    result += current
                }
            } else {
                result += current
            }
        }

        return result.filterNot { it.text.isEmpty() }
    }

    /**
     * Adds plain text token if [currentTokenContent] is not empty
     */
    private fun addPlainTextTokenIfAny(
        currentTokenContent: StringBuilder,
        result: MutableList<Token>
    ) {
        if (currentTokenContent.isNotEmpty()) {
            result += Token(currentTokenContent.toString(), Token.Type.Plain)
            currentTokenContent.clear()
        }
    }

    /**
     * Greedy parser of text link *bold* or _italics_.
     */
    private fun parseText(
        iterator: CharIterator,
        startCharacter: Char,
        stopCharacter: Char,
        tokenType: Token.Type
    ): Token {
        val content = StringBuilder()
        do {
            val nextChar = iterator.nextChar()
            if (nextChar == stopCharacter) {
                return Token(content.toString(), tokenType)
            } else {
                content.append(nextChar)
            }
        } while (iterator.hasNext())

        // text was not finished with stop character, it is considered as plain
        return Token(startCharacter + content.toString(), Token.Type.Plain)
    }

    /**
     * Link parser. Link consists of [Text part] and (url part).
     */
    private fun parseLink(iterator: CharIterator): Token {
        val currentTokenContent = StringBuilder()
        do {
            val nextChar = iterator.nextChar()
            if (nextChar == LINK_TEXT_STOP) {
                // currentTokenContent now contains text of the link
                // lets parse url
                when (val parsingResult = parseUrl(iterator)) {
                    // we were able to parse url, lets finish token
                    is UrlParsingResult.Url -> {
                        return Token(currentTokenContent.toString(), Token.Type.Link(parsingResult.url))
                    }
                    // text was not well-formed we treat it as plain text
                    is UrlParsingResult.PlainText -> {
                        currentTokenContent.append("$LINK_TEXT_STOP${parsingResult.text}")
                    }
                    // there was no url information
                    is UrlParsingResult.NoUrl -> {
                        currentTokenContent.append(LINK_TEXT_STOP)
                    }
                }
            } else {
                currentTokenContent.append(nextChar)
            }
        } while (iterator.hasNext())

        // text was not finished with stop character, it is considered as plain
        return Token("$LINK_TEXT_START$currentTokenContent", Token.Type.Plain)
    }

    /** Holds possible results returned from [parseUrl]. */
    private sealed class UrlParsingResult {
        data class Url(val url: String) : UrlParsingResult()

        data class PlainText(val text: String) : UrlParsingResult()

        object NoUrl : UrlParsingResult()
    }

    /**
     * Parses URL.
     */
    private fun parseUrl(iterator: CharIterator): UrlParsingResult {
        return if (iterator.hasNext()) {
            val urlOpeningCharacter = iterator.nextChar()
            if (urlOpeningCharacter == LINK_URL_START) {
                parseUrlValue(iterator)
            } else {
                UrlParsingResult.PlainText(urlOpeningCharacter.toString())
            }
        } else {
            // there is no character left, so no URL info can be retrieved
            UrlParsingResult.NoUrl
        }
    }

    /**
     * Parses URL value.
     */
    private fun parseUrlValue(iterator: CharIterator): UrlParsingResult {
        val url = StringBuilder()
        // let's parse until stop character is found
        do {
            val nextCharFromUrl = iterator.nextChar()
            if (nextCharFromUrl == LINK_URL_STOP) {
                // stop found let's return url
                return UrlParsingResult.Url(url.toString())
            } else {
                url.append(nextCharFromUrl)
            }
        } while (iterator.hasNext())

        // no stop found, we will treat it as plain text
        return UrlParsingResult.PlainText("$LINK_URL_START$url")
    }
}
